import { isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { BehaviorSubject, Observable, shareReplay } from "rxjs";
import { GlobalFunctions } from "./global-functions";
import { CONSTANTS } from "./constants";
import { LocalStorageHelper } from "./helpers/local-storage.helper";

@Injectable({
  providedIn: 'root'
})

export class CommonService {

  constructor(
    private _httpClient: HttpClient,
    private _globalFunctions: GlobalFunctions,
    private _localStorageHelper: LocalStorageHelper,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  private profileCache$: { [role: string]: Observable<any> } = {};
  private profileStatusCache$?: Observable<any>;
  
  // Reactive State for Student & Tutor Profiles
  private studentProfileSubject = new BehaviorSubject<any>(null);
  public studentProfile$ = this.studentProfileSubject.asObservable();
  
  private tutorProfileSubject = new BehaviorSubject<any>(null);
  public tutorProfile$ = this.tutorProfileSubject.asObservable();
  
  private profileStatusSubject = new BehaviorSubject<any>(null);
  public profileStatus$ = this.profileStatusSubject.asObservable();

  getProfile(isStudent: boolean = false, force: boolean = false): Observable<any> {
    const role = isStudent ? 'student' : 'tutor';
    if (force) {
      this.profileStatusCache$ = undefined;
    }
    if (!this.profileCache$[role] || force) {
      this.profileCache$[role] = this._httpClient.get(role + '/profile').pipe(
        shareReplay(1)
      );
    }

    // Capture the stream and push to Subject and LocalStorage
    this.profileCache$[role].subscribe(res => {
      if (res && res.success) {
        if (isStudent) {
            this.studentProfileSubject.next(res.data);
        } else {
            this.tutorProfileSubject.next(res.data);
        }
        this.setProfileData(res.data);
      }
    });

    return this.profileCache$[role];
  }

  /**
   * Manually refresh the student profile and notify all subscribers
   */
  refreshProfile() {
    return this.getProfile(true, true).subscribe();
  }

  getProfileStatus(force: boolean = false): Observable<any> {
    if (!this.profileStatusCache$ || force) {
      this.profileStatusCache$ = this._httpClient.get('tutor/profile/status').pipe(shareReplay(1));
      
      this.profileStatusCache$.subscribe(res => {
        if (res && res.success) {
          this.profileStatusSubject.next(res.data);
        }
      });
    }
    return this.profileStatusCache$;
  }

  getUser(): Observable<any> {
    return this._httpClient.get('user');
  }

  setProfileData(profileObj: any) {
    if (isPlatformBrowser(this.platformId)) {
      this._localStorageHelper.setItem('profileObj', JSON.stringify(profileObj));
    }
  }

  getProfileDataFromLocalStorage() {
    if (isPlatformBrowser(this.platformId) && this._localStorageHelper.getItem('profileObj')) {
      return JSON.parse(this._localStorageHelper.getItem('profileObj')!)!;
    }
  }

  getSubjects(data: any = {}): Observable<any> {
    const params = this.buildQueryParams(data);
    return this._httpClient.get('subjects', { params });
  }

  getLanguages(data: any = {}): Observable<any> {
    if (!data.limit) data.limit = 30;
    const params = this.buildQueryParams(data);
    return this._httpClient.get('languages', { params });
  }

  clearCache() {
    this.profileCache$ = {};
    this.profileStatusCache$ = undefined;
  }

  buildQueryParams(filter: any): HttpParams {
    let params = new HttpParams();
    Object.keys(filter).forEach(key => {
      const value = filter[key];
      if (Array.isArray(value)) {
        value.forEach(v => params = params.append(key, v));
      } else if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value);
      }
    });
    return params;
  }
}